/**
 * EditableCollectionBinding
 *
 * Edit-mode wrapper for a collection-bound region (gallery carousel, amenities
 * list, etc.). Clicking it opens the parent component editor scrolled to that
 * binding — same deep-link pattern tokens/slot groups already use.
 */

import { useCallback, type ReactNode, type CSSProperties } from 'react';

import {
  useEditMode,
  postScopeTargetSelected,
  postCommentPinTargetSelected,
  type ComponentSelectPayload,
} from './edit-mode';
import { useEditableRegion, EditableRegionHoverLabel, editableRegionStyles } from './editable-region';
import { useSectionEditInfo } from './editable-section';

export interface EditableCollectionBindingProps {
  /** Binding path (e.g. "amenitieslist.apartmentPhotos") */
  bindingPath: string;
  /** Hover label; defaults to the last path segment */
  label?: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function EditableCollectionBinding({
  bindingPath,
  label,
  children,
  className = '',
  style,
}: EditableCollectionBindingProps) {
  const editMode = useEditMode();
  const sectionEditInfo = useSectionEditInfo();

  const isEditMode = editMode?.isEditMode ?? false;
  const displayLabel =
    label || bindingPath.split('.').pop()?.replace(/([A-Z])/g, ' $1').trim() || bindingPath;

  const handleSelect = useCallback(
    (region: HTMLElement) => {
      if (!isEditMode || !editMode || !sectionEditInfo) return;

      const rect = region.getBoundingClientRect();
      const componentInfo = {
        ...sectionEditInfo,
        bounds: {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        },
      };

      if (editMode.scopeMode) {
        postScopeTargetSelected({
          kind: 'section',
          componentInfo,
        });
        return;
      }
      if (editMode.commentPinMode) {
        postCommentPinTargetSelected({
          kind: 'section',
          componentInfo,
        });
        return;
      }

      // Open the parent component editor focused on this binding.
      const payload: ComponentSelectPayload = {
        ...componentInfo,
        highlightTokenPath: bindingPath,
      };
      editMode.selectComponent(payload);
    },
    [isEditMode, editMode, sectionEditInfo, bindingPath],
  );

  const { regionRef, isHovered, regionProps } = useEditableRegion({
    enabled: isEditMode,
    // Nested tokens / slot groups keep their own editors.
    nestedEditableSelector: '[data-vibesite-token], [data-vibesite-slot-group]',
    onSelect: handleSelect,
  });

  if (!editMode || !isEditMode) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  const rect = isHovered ? regionRef.current?.getBoundingClientRect() : null;

  return (
    <>
      <div
        ref={regionRef}
        className={`vibesite-editable-region vibesite-editable-collection-binding ${className}`}
        data-vibesite-collection-binding={bindingPath}
        data-vibesite-region-hovered={isHovered}
        {...regionProps}
        style={{
          cursor: 'pointer',
          ...style,
        }}
      >
        {children}
        <style>{editableRegionStyles}</style>
      </div>

      {isHovered && rect && (
        <EditableRegionHoverLabel
          rect={rect}
          style={{
            marginTop: -4,
            background: 'rgba(119, 59, 255, 0.95)',
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            zIndex: 100000,
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          {displayLabel}
        </EditableRegionHoverLabel>
      )}
    </>
  );
}

export default EditableCollectionBinding;
